"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.expenseFieldMapping = exports.expenseSchema = void 0;
const amount_utils_1 = require("../../amount-utils");
exports.expenseSchema = {
    fields: {
        hourly_expense: {
            maxLength: 259,
            pattern: amount_utils_1.FORMATTED_AMOUNT_PATTERN,
            custom: {
                validate: (value) => {
                    // If hourly_expense is provided, it must be > 0
                    if (value && value.toString().trim() !== '') {
                        return (0, amount_utils_1.isPositiveAmount)(value);
                    }
                    return true; // Valid if not provided (cross-field validation handles requirement)
                },
                message: 'Hourly expense must be greater than 0',
            },
        },
        hour_per_week: {
            maxLength: 259,
            pattern: /^\d+$/,
            conditionalRequired: {
                condition: (formValues) => {
                    const hourlyExpense = formValues.hourly_expense;
                    return hourlyExpense && hourlyExpense.toString().trim() !== '';
                },
                message: 'Hours per week is required',
            },
            custom: {
                validate: (value) => {
                    // If hour_per_week is provided, it must be > 0
                    if (value && value.toString().trim() !== '') {
                        const numValue = parseInt(value);
                        return !isNaN(numValue) && numValue > 0;
                    }
                    return true;
                },
                message: 'Hours per week must be greater than 0',
            },
        },
        week_per_year: {
            maxLength: 259,
            pattern: /^\d+$/,
            conditionalRequired: {
                condition: (formValues) => {
                    const hourlyExpense = formValues.hourly_expense;
                    return hourlyExpense && hourlyExpense.toString().trim() !== '';
                },
                message: 'Weeks per year is required',
            },
            custom: {
                validate: (value) => {
                    // If week_per_year is provided, it must be > 0
                    if (value && value.toString().trim() !== '') {
                        const numValue = parseInt(value);
                        return !isNaN(numValue) && numValue > 0;
                    }
                    return true;
                },
                message: 'Weeks per year must be greater than 0',
            },
        },
        expense_amount: {
            maxLength: 259,
            pattern: amount_utils_1.FORMATTED_AMOUNT_PATTERN,
            custom: {
                validate: (value) => {
                    // If expense_amount is provided, it must be > 0
                    if (value && value.toString().trim() !== '') {
                        return (0, amount_utils_1.isPositiveAmount)(value);
                    }
                    return true; // Valid if not provided (cross-field validation handles requirement)
                },
                message: 'Expense amount must be greater than 0',
            },
            crossFieldValidation: {
                dependentFields: ['hourly_expense'],
                validate: (value, allValues) => {
                    const hasExpenseAmount = value && value.toString().trim() !== '' && (0, amount_utils_1.parseAmount)(value) > 0;
                    const hasHourlyExpense = allValues.hourly_expense &&
                        allValues.hourly_expense.toString().trim() !== '' &&
                        (0, amount_utils_1.parseAmount)(allValues.hourly_expense) > 0;
                    // At least one of the two must be provided with a valid value > 0
                    if (!hasExpenseAmount && !hasHourlyExpense) {
                        return 'A valid expense amount is required';
                    }
                    return true;
                },
            },
        },
        description: {
            maxLength: 240,
        },
        institution_name: {
            maxLength: 50,
        },
        contact_name: {
            maxLength: 50,
        },
        contact_email: {
            maxLength: 74,
        },
        institution_street_line1: {
            maxLength: 100,
        },
        institution_street_line2: {
            maxLength: 100,
        },
        institution_street_line3: {
            maxLength: 100,
        },
        institution_city: {
            maxLength: 50,
        },
        institution_postal_code: {
            maxLength: 20,
        },
        date_started: {
            custom: {
                validate: (value) => {
                    if (!value)
                        return true; // Optional field
                    const inputDate = new Date(value);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999); // End of today
                    return inputDate <= today;
                },
                message: 'Start date must be today or earlier',
            },
        },
    },
    messages: {
        hourly_expense: {
            pattern: 'Hourly expense must be a valid numeric value',
            custom: 'Hourly expense must be greater than 0',
        },
        hour_per_week: {
            pattern: 'Hours per week must be a valid numeric value',
            conditionalRequired: 'Hours per week is required',
            custom: 'Hours per week must be greater than 0',
        },
        week_per_year: {
            pattern: 'Weeks per year must be a valid numeric value',
            conditionalRequired: 'Weeks per year is required',
            custom: 'Weeks per year must be greater than 0',
        },
        expense_amount: {
            pattern: 'Expense amount must be a valid numeric value',
            custom: 'Expense amount must be greater than 0',
        },
        description: {
            maxLength: 'Description must be 240 characters or less',
        },
        institution_name: {
            maxLength: 'Provider Name must be 50 characters or less',
        },
        contact_name: {
            maxLength: 'Contact name must be 50 characters or less',
        },
        contact_email: {
            maxLength: 'Contact email must be 74 characters or less',
        },
        institution_street_line1: {
            maxLength: 'Address Line 1 must be 100 characters or less',
        },
        institution_street_line2: {
            maxLength: 'Address Line 2 must be 100 characters or less',
        },
        institution_street_line3: {
            maxLength: 'Address Line 3 must be 100 characters or less',
        },
        institution_city: {
            maxLength: 'City must be 50 characters or less',
        },
        institution_postal_code: {
            maxLength: 'Zip Code must be 20 characters or less',
        },
        date_started: {
            custom: 'Start Date must be today or earlier',
        },
    },
};
// Field mapping between DTO naming and UI naming
exports.expenseFieldMapping = {
    hourly_expense: 'hourly_expense',
    hour_per_week: 'hour_per_week',
    week_per_year: 'week_per_year',
    expense_amount: 'expense_amount',
    description: 'description',
    institution_name: 'institution_name',
    contact_name: 'contact_name',
    contact_email: 'contact_email',
    institution_street_line1: 'institution_street_line1',
    institution_street_line2: 'institution_street_line2',
    institution_street_line3: 'institution_street_line3',
    institution_city: 'institution_city',
    institution_postal_code: 'institution_postal_code',
    date_started: 'date_started',
    date_ended: 'date_ended',
};
